import { Timestamp } from 'firebase/firestore';

export type GameRole = 'player' | 'gm';
export type GameFrequency = 'Weekly' | 'Fortnightly' | 'Monthly' | 'Ad-hoc' | 'One Shot';
export type GameTable = 'Foundry VTT' | 'Roll20' | 'Fantasy Grounds' | 'Other';
export type GameComms = 'Discord' | 'TeamSpeak' | 'Guilded' | 'Mumble';

// Server-side (stored in Firestore, contains userId)
export interface GamePlayer {
	userId: string;
	alias: string;
	role: GameRole;
	characterId?: string;
	characterName?: string;
}

// Client-side (returned from Cloud Functions, no userId)
export interface GamePlayerClient {
	alias: string;
	role: GameRole;
	characterId?: string;
	characterName?: string;
}

export interface Game {
	id: string;
	name: string;
	description: string;
	imageUrl: string;
	createdBy: string;
	createdByAlias: string;
	startTime: Timestamp | null;
	duration: number; // hours (1-8)
	players: GamePlayer[];
	playerIds: string[];
	tags: string[];
	cost: string;
	frequency: GameFrequency | '';
	system: string;
	table: GameTable | '';
	comms: GameComms | '';
	utils: string;
	rank: number;
	progression: string;
	commitment: string;
	rpRatio: string;
	backstoryRules: string;
	currentCores: string[];
	createdAt: Timestamp | null;

	// Campaign module + character-creation gating (added for the module system).
	// `moduleId` is the id of a CampaignModule (see data/campaignModules.ts), or ''
	// for a custom/other game with no module. The three allow-lists hold property
	// ids (origin / legacy / homeWorld). An EMPTY list means "no restriction, all
	// allowed" so pre-existing games keep working unchanged.
	moduleId: string;
	allowedRaces: string[];
	allowedLegacies: string[];
	allowedHomeworlds: string[];

	// Invite links. Set by the GM; only revealed to a player once their
	// application to this game has been accepted (they are in `players`).
	// The VTT invite URL joins the campaign; the game URL is the direct/launch
	// link; the comms invite URL joins the voice/chat server (e.g. Discord).
	fgInviteUrl: string;
	fgGameUrl: string;
	commsInviteUrl: string;

	// Who can see this game's open recruitment. 'public' = anyone; 'friends' =
	// only the creator's friends (matched by alias). Default 'public'.
	recruitmentVisibility: 'public' | 'friends';

	// When true, characters that violate the game's restrictions (rank / allowed
	// races / legacies / homeworlds / module) CANNOT be submitted; the player is
	// told exactly what fails. When false, mismatches only warn. Default false.
	enforceRestrictions: boolean;
}

export const defaultGame = (): Game => ({
	id: '',
	name: '',
	description: '',
	imageUrl: '',
	createdBy: '',
	createdByAlias: '',
	startTime: null,
	duration: 3,
	players: [],
	playerIds: [],
	tags: [],
	cost: '',
	frequency: '',
	system: 'Cybercore',
	table: '',
	comms: '',
	utils: '',
	rank: 1,
	progression: '',
	commitment: '',
	rpRatio: '',
	backstoryRules: '',
	currentCores: [],
	createdAt: null,
	moduleId: '',
	allowedRaces: [],
	allowedLegacies: [],
	allowedHomeworlds: [],
	fgInviteUrl: '',
	fgGameUrl: '',
	commsInviteUrl: '',
	recruitmentVisibility: 'public',
	enforceRestrictions: false,
});
